import React, { useState } from "react";
import { MdClose } from "react-icons/md";
import { FaRegCheckSquare, FaRegSquare } from "react-icons/fa";
import { CircularProgress } from "@mui/material";
import tw from "tailwind-styled-components";
import { GoSearch } from "react-icons/go";
import { LuBed } from "react-icons/lu";
import { FaRupeeSign } from "react-icons/fa";
import { ScrollableContent } from "../../Styles/InputStyles";

const RoomDropdownModal = ({
    isOpen,
    onClose,
    title = "Select Room",
    options = [],
    selectedValue,
    onSelect,
    isLoading = false,
    searchPlaceholder = "Search rooms..."
}) => {
    const [searchText, setSearchText] = useState("");

    if (!isOpen) return null;

    const filteredOptions = options.filter((option) =>
        option.roomNum?.toLowerCase().includes(searchText.toLowerCase()) ||
        option.floor?.toLowerCase().includes(searchText.toLowerCase()) ||
        option.flatName?.toLowerCase().includes(searchText.toLowerCase())
    );

    const handleSelect = (value) => {
        onSelect(value);
        onClose();
    };

    const isSelected = (value) => {
        return selectedValue === value.id;
    };

    return (
        <Overlay>
            <ModalWrapper>
                <Header>
                    <h1 className="text-white text-lg font-medium">{title}</h1>
                    <button onClick={onClose}>
                        <MdClose color="white" size={24} />
                    </button>
                </Header>

                {/* Search Box */}
                <div className="px-4 pt-4 relative">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder={searchPlaceholder}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-full px-8 h-12 py-2 border border-gray-200 rounded-md focus:outline-none focus:border-primary-color text-gray-800 placeholder-gray-300"
                        />
                        {/* Search Icon */}
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                            <GoSearch className="w-4 h-4 text-gray-400" />
                        </div>
                    </div>
                </div>

                <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <CircularProgress />
                        </div>
                    ) : filteredOptions.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            {searchText ? "No rooms found" : "No rooms available"}
                        </div>
                    ) : (
                        filteredOptions.map((option) => (
                            <RoomCard
                                key={option.id}
                                onClick={() => !option.disabled && handleSelect(option)}
                                className={isSelected(option) ? "bg-blue-50 border-blue-200" : ""}
                                disabled={option.disabled}
                            >
                                <RoomHeader>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2">
                                                <RoomNumber>#{option.roomNum}</RoomNumber>
                                                {option.flatId ? (
                                                    <RoomFloor>Flat {option.flatName}</RoomFloor>
                                                ) : (
                                                    <RoomFloor>Floor {option.floor}</RoomFloor>
                                                )}
                                            </div>
                                            {isSelected(option) ? (
                                                <FaRegCheckSquare size={20} className="text-primary-color" />
                                            ) : (
                                                <FaRegSquare size={20} className="text-gray-300" />
                                            )}
                                        </div>
                                    </div>
                                </RoomHeader>

                                <RoomDetails>
                                    <DetailItem>
                                        <DetailLabel>Monthly Rent</DetailLabel>
                                        <DetailValue className="text-primary-color font-medium">
                                            <FaRupeeSign className="inline text-xs mr-0.5" />
                                            {option.roomPrice || "0"}
                                        </DetailValue>
                                    </DetailItem>

                                    <DetailItem>
                                        <DetailLabel>Beds Status</DetailLabel>
                                        <div className="flex items-center gap-4">
                                            <BedStatus>
                                                <LuBed size={14} className="mr-1" />
                                                <span className="text-sm">Vacant: </span>
                                                <span className="font-medium">{option.vacantBeds || 0}</span>
                                            </BedStatus>
                                            <BedStatus>
                                                <LuBed size={14} className="mr-1" />
                                                <span className="text-sm">Total: </span>
                                                <span className="font-medium">{option.totalBeds || 0}</span>
                                            </BedStatus>
                                        </div>
                                    </DetailItem>
                                </RoomDetails>
                            </RoomCard>
                        ))
                    )}
                </ScrollableContent>
            </ModalWrapper>
        </Overlay>
    );
};

const Overlay = tw.div`fixed inset-0 z-50 flex items-center justify-end bg-black/50`
const ModalWrapper = tw.div`bg-white w-11/12 h-full flex flex-col relative`
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`
const RoomCard = tw.div`
    mx-4 my-3 p-4 border border-gray-200 rounded-lg cursor-pointer 
    hover:bg-gray-50 hover:border-gray-300 transition-all duration-150
    ${props => props.disabled ? 'opacity-50 cursor-not-allowed' : ''}
`;
const RoomHeader = tw.div`flex justify-between items-start mb-3`;
const RoomNumber = tw.span`text-lg font-semibold text-gray-800`;
const RoomFloor = tw.span`text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full`;
const RoomDetails = tw.div`space-y-2`;
const DetailItem = tw.div`flex justify-between items-center`;
const DetailLabel = tw.span`text-sm text-gray-500`;
const DetailValue = tw.span`text-sm text-gray-700`;
const BedStatus = tw.div`flex items-center text-gray-600`;

export default RoomDropdownModal;